/**
 * Luxe DesignScene — canonical component registry.
 *
 * Mirrors /app/backend/scene_service.py::COMPONENT_REGISTRY. The
 * backend remains authoritative (server-side validation via
 * PATCH /projects/{pid}/scene), but the UI ships the same map so the
 * palette and Properties Panel can render without a network round-trip.
 *
 * Adding a new component here does NOT require touching the
 * SchematicBuilder core — the palette groups by category automatically
 * and the Properties Panel reads defaults + allowed quantity modes.
 *
 * Coordinate/semantic conventions:
 *   X = horizontal along wall (mm), 0 at left, positive to the right
 *   Y = height above finished floor (mm), 0 at floor, positive up
 *   Z = projection from wall face (mm), 0 at wall plane, positive
 *       TOWARD the room. `depth` is the object's Z-extent.
 *   Recess: semantic="recess" — the void extends INTO the wall (i.e.
 *   BEHIND the wall face). Future 3D consumes semantic_type to decide
 *   between projection and boolean subtraction; no inference required.
 */
import {
  Tv, Flame, Box, Layers, Square, Lightbulb, Plug, Cable, Radio,
  BoxSelect, ListMinus, Speaker, Volume2, PanelTop, Rows,
  Mountain, Trees, Waves, Palette,
} from "lucide-react";


export const COMPONENT_REGISTRY = {
  // ── Structure ────────────────────────────────────────────────────
  wall_return:      { category: "Structure",     label: "Wall Return",       icon: BoxSelect, w: 300, h: 2400, d: 300, semantic: "solid",   default_qm: "area_m2", allowed_qms: ["area_m2", "linear_m", "each"], color: "#6B7280" },
  bulkhead:         { category: "Structure",     label: "Bulkhead",           icon: PanelTop, w: 3000, h: 300, d: 400, semantic: "solid",   default_qm: "linear_m", allowed_qms: ["linear_m", "area_m2", "each"], color: "#6B7280" },
  // ── Media ────────────────────────────────────────────────────────
  tv:               { category: "Media",         label: "TV",                 icon: Tv,       w: 1500, h: 900,  d: 60,  semantic: "media",   default_qm: "each",     allowed_qms: ["each"], color: "#111827" },
  tv_recess:        { category: "Media",         label: "TV Recess",          icon: BoxSelect, w: 1850, h: 1100, d: 120, semantic: "recess",  default_qm: "each",     allowed_qms: ["each", "area_m2"], color: "#374151", dashed: true },
  speaker:          { category: "Media",         label: "Speaker",            icon: Speaker,  w: 220,  h: 350,  d: 180, semantic: "media",   default_qm: "each",     allowed_qms: ["each"], color: "#1F2937" },
  soundbar:         { category: "Media",         label: "Soundbar",           icon: Volume2,  w: 1200, h: 90,   d: 90,  semantic: "media",   default_qm: "each",     allowed_qms: ["each"], color: "#1F2937" },
  // ── Fireplace ────────────────────────────────────────────────────
  fireplace:        { category: "Fireplace",     label: "Fireplace",          icon: Flame,    w: 1100, h: 500,  d: 250, semantic: "media",   default_qm: "each",     allowed_qms: ["each"], color: "#A16207" },
  fireplace_recess: { category: "Fireplace",     label: "Fireplace Recess",   icon: BoxSelect, w: 1300, h: 700, d: 300, semantic: "recess",  default_qm: "each",     allowed_qms: ["each", "area_m2"], color: "#78350F", dashed: true },
  // ── Cabinetry ────────────────────────────────────────────────────
  floating_cabinet: { category: "Cabinetry",     label: "Floating Cabinet",   icon: Box,      w: 2400, h: 450,  d: 450, semantic: "solid",   default_qm: "each",     allowed_qms: ["each", "linear_m"], color: "#57534E", hasClearances: true },
  base_cabinet:     { category: "Cabinetry",     label: "Base Cabinet",       icon: Box,      w: 1200, h: 720,  d: 600, semantic: "solid",   default_qm: "each",     allowed_qms: ["each", "linear_m"], color: "#57534E", hasClearances: true },
  tall_cabinet:     { category: "Cabinetry",     label: "Tall Cabinet",       icon: Box,      w: 900,  h: 2100, d: 600, semantic: "solid",   default_qm: "each",     allowed_qms: ["each", "linear_m"], color: "#44403C", hasClearances: true },
  drawer_unit:      { category: "Cabinetry",     label: "Drawer Unit",        icon: Rows,     w: 900,  h: 450,  d: 550, semantic: "solid",   default_qm: "each",     allowed_qms: ["each", "linear_m"], color: "#57534E", hasClearances: true },
  // ── Architectural ────────────────────────────────────────────────
  shelf:            { category: "Architectural", label: "Shelf",              icon: Layers,   w: 900,  h: 40,   d: 250, semantic: "solid",   default_qm: "linear_m", allowed_qms: ["linear_m", "each"], color: "#8B8785" },
  niche:            { category: "Architectural", label: "Niche",              icon: BoxSelect, w: 400, h: 600,  d: 200, semantic: "recess",  default_qm: "each",     allowed_qms: ["each", "area_m2"], color: "#3F3F46", dashed: true },
  feature_panel:    { category: "Architectural", label: "Feature Panel",      icon: Square,   w: 1200, h: 2400, d: 18,  semantic: "surface", default_qm: "area_m2",  allowed_qms: ["area_m2", "each"], color: "#3A4D39" },
  stone_panel:      { category: "Architectural", label: "Stone Panel",        icon: Mountain, w: 1200, h: 2400, d: 20,  semantic: "surface", default_qm: "area_m2",  allowed_qms: ["area_m2"], color: "#78716C" },
  timber_panel:     { category: "Architectural", label: "Timber Panel",       icon: Trees,    w: 1200, h: 2400, d: 18,  semantic: "surface", default_qm: "area_m2",  allowed_qms: ["area_m2"], color: "#78350F" },
  acoustic_panel:   { category: "Architectural", label: "Acoustic Panel",     icon: Waves,    w: 600,  h: 600,  d: 25,  semantic: "surface", default_qm: "area_m2",  allowed_qms: ["area_m2"], color: "#525252" },
  cladding:         { category: "Architectural", label: "Cladding",           icon: Palette,  w: 2400, h: 2400, d: 20,  semantic: "surface", default_qm: "area_m2",  allowed_qms: ["area_m2"], color: "#404040" },
  // ── Services ─────────────────────────────────────────────────────
  led_strip:        { category: "Services",      label: "LED Strip",          icon: Lightbulb, w: 1200, h: 12, d: 10,  semantic: "light",   default_qm: "linear_m", allowed_qms: ["linear_m"], color: "#F59E0B" },
  power_point:      { category: "Services",      label: "Single GPO",         icon: Plug,     w: 90,   h: 90,   d: 40,  semantic: "service", default_qm: "each",     allowed_qms: ["each"], color: "#DC2626", is_symbol: true, symbol_type: "gpo_single", legend_code: "GPO", symbol_px: 22 },
  data_point:       { category: "Services",      label: "Data Outlet",        icon: Cable,    w: 90,   h: 90,   d: 40,  semantic: "service", default_qm: "each",     allowed_qms: ["each"], color: "#2563EB", is_symbol: true, symbol_type: "data_outlet", legend_code: "D", symbol_px: 22 },
  gpo_double:       { category: "Services",      label: "Double GPO",         icon: Plug,     w: 120,  h: 90,   d: 40,  semantic: "service", default_qm: "each",     allowed_qms: ["each"], color: "#DC2626", is_symbol: true, symbol_type: "gpo_double", legend_code: "GPO2", symbol_px: 26 },
  switch_single:    { category: "Services",      label: "Light Switch",       icon: Plug,     w: 90,   h: 90,   d: 40,  semantic: "service", default_qm: "each",     allowed_qms: ["each"], color: "#0F172A", is_symbol: true, symbol_type: "switch_single", legend_code: "S", symbol_px: 20 },
  switch_multi:     { category: "Services",      label: "Multi-Gang Switch",  icon: Plug,     w: 120,  h: 90,   d: 40,  semantic: "service", default_qm: "each",     allowed_qms: ["each"], color: "#0F172A", is_symbol: true, symbol_type: "switch_multi", legend_code: "S/n", symbol_px: 22 },
  dimmer:           { category: "Services",      label: "Dimmer",             icon: Plug,     w: 90,   h: 90,   d: 40,  semantic: "service", default_qm: "each",     allowed_qms: ["each"], color: "#0F172A", is_symbol: true, symbol_type: "dimmer", legend_code: "SD", symbol_px: 20 },
  downlight:        { category: "Services",      label: "Downlight",          icon: Lightbulb, w: 90,  h: 90,   d: 90,  semantic: "light",   default_qm: "each",     allowed_qms: ["each"], color: "#F59E0B", is_symbol: true, symbol_type: "downlight", legend_code: "DL", symbol_px: 22 },
  wall_light:       { category: "Services",      label: "Wall Light",         icon: Lightbulb, w: 90,  h: 90,   d: 120, semantic: "light",   default_qm: "each",     allowed_qms: ["each"], color: "#F59E0B", is_symbol: true, symbol_type: "wall_light", legend_code: "WL", symbol_px: 20 },
};

export const CATEGORIES = ["Structure", "Media", "Fireplace", "Cabinetry", "Architectural", "Services"];

// ── Canonical Material Slot definitions (frontend mirror). Backend
// is authoritative — see /app/backend/scene_service.py::_SLOT_DEFS.
// Only cabinetry ships with multi-slot definitions in Phase 1A. Other
// components use the primary material_id.
export const CABINET_SLOTS = {
  front:   { label: "Front Finish", required: true,  allowed_categories: ["laminate", "veneer", "decorative_board", "painted", "melamine"], inherit_primary: false },
  carcass: { label: "Carcass",      required: true,  allowed_categories: ["cabinet_board", "moisture_board", "melamine"], inherit_primary: false },
  top:     { label: "Top",          required: false, allowed_categories: ["stone", "porcelain", "compact_laminate", "timber"], inherit_primary: false },
};
export const SLOT_DEFS = {
  floating_cabinet: CABINET_SLOTS,
  base_cabinet:     CABINET_SLOTS,
  tall_cabinet:     CABINET_SLOTS,
  drawer_unit:      { front: CABINET_SLOTS.front, carcass: CABINET_SLOTS.carcass },
};

export function slotsFor(type) {
  return SLOT_DEFS[type] || {};
}

// Primary-material allowed categories for single-material components.
// Empty array means "no restriction" — Search All is always the default.
export const PRIMARY_CATEGORIES = {
  feature_panel:   ["decorative_board", "timber", "acoustic", "microcement", "venetian"],
  stone_panel:     ["stone", "porcelain"],
  timber_panel:    ["timber", "decorative_board"],
  acoustic_panel:  ["acoustic"],
  cladding:        ["cladding", "timber", "stone"],
  shelf:           ["timber", "decorative_board", "stone"],
  // Manufactured products (media + services) are keyed to product categories.
  tv:              ["av_tv"],
  speaker:         ["av_speaker"],
  soundbar:        ["av_soundbar"],
  fireplace:       ["fireplace"],
  led_strip:       ["lighting_led"],
  power_point:     ["electrical_gpo"],
  data_point:      ["electrical_data"],
};

export function primaryCategoriesFor(type) {
  return PRIMARY_CATEGORIES[type] || [];
}

export function getComponent(type) {
  return COMPONENT_REGISTRY[type] || null;
}

export function componentsByCategory() {
  const grouped = {};
  for (const cat of CATEGORIES) grouped[cat] = [];
  for (const [type, def] of Object.entries(COMPONENT_REGISTRY)) {
    (grouped[def.category] || (grouped[def.category] = [])).push({ type, ...def });
  }
  return grouped;
}

// ── Wall-size-responsive preset generators ────────────────────────────
// Presets return NEW arrays of scene items scaled to actual wall
// dimensions — never hard-coded pixel positions.
export function buildPreset(name, wallW, wallH) {
  const mkId = (t) => `it_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}_${t}`;
  const centre = (w) => Math.max(0, (wallW - w) / 2);
  const tvY = Math.max(0, wallH * 0.42);

  switch (name) {
    case "tv_floating_cabinet": {
      const cab = COMPONENT_REGISTRY.floating_cabinet;
      const tv = COMPONENT_REGISTRY.tv;
      const cabW = Math.min(cab.w, wallW * 0.6);
      const tvW = Math.min(tv.w, wallW * 0.4);
      return [
        { id: mkId("tv"),               type: "tv",               x: centre(tvW),  y: tvY, z: 0, width: tvW,  height: tv.h,  depth: tv.d,  rotation: 0, semantic_type: "media" },
        { id: mkId("floating_cabinet"), type: "floating_cabinet", x: centre(cabW), y: 600, z: 0, width: cabW, height: cab.h, depth: cab.d, rotation: 0, semantic_type: "solid" },
      ];
    }
    case "tv_fireplace": {
      const fp = COMPONENT_REGISTRY.fireplace;
      const tv = COMPONENT_REGISTRY.tv;
      const tvW = Math.min(tv.w, wallW * 0.4);
      const fpW = Math.min(fp.w, wallW * 0.35);
      return [
        { id: mkId("fireplace"), type: "fireplace", x: centre(fpW), y: 250,  z: 0, width: fpW, height: fp.h,  depth: fp.d,  rotation: 0, semantic_type: "media" },
        { id: mkId("tv"),        type: "tv",        x: centre(tvW), y: 1250, z: 0, width: tvW, height: tv.h,  depth: tv.d,  rotation: 0, semantic_type: "media" },
      ];
    }
    case "tv_fireplace_base_cabinet": {
      const fp = COMPONENT_REGISTRY.fireplace;
      const tv = COMPONENT_REGISTRY.tv;
      const bc = COMPONENT_REGISTRY.base_cabinet;
      const tvW = Math.min(tv.w, wallW * 0.4);
      const fpW = Math.min(fp.w, wallW * 0.35);
      const bcW = Math.min(wallW * 0.9, bc.w * 2);
      return [
        { id: mkId("base_cabinet"), type: "base_cabinet", x: centre(bcW), y: 0,    z: 0, width: bcW, height: bc.h, depth: bc.d, rotation: 0, semantic_type: "solid" },
        { id: mkId("fireplace"),    type: "fireplace",    x: centre(fpW), y: 900,  z: 0, width: fpW, height: fp.h, depth: fp.d, rotation: 0, semantic_type: "media" },
        { id: mkId("tv"),           type: "tv",           x: centre(tvW), y: 1600, z: 0, width: tvW, height: tv.h, depth: tv.d, rotation: 0, semantic_type: "media" },
      ];
    }
    case "full_height_feature_wall": {
      const p = COMPONENT_REGISTRY.feature_panel;
      const led = COMPONENT_REGISTRY.led_strip;
      return [
        { id: mkId("feature_panel"), type: "feature_panel", x: 0, y: 0, z: 0, width: wallW, height: wallH, depth: p.d,   rotation: 0, semantic_type: "surface" },
        { id: mkId("led_strip"),     type: "led_strip",     x: 0, y: Math.max(0, wallH - 30), z: 0, width: wallW, height: led.h, depth: led.d, rotation: 0, semantic_type: "light" },
      ];
    }
    default:
      return [];
  }
}

export const PRESETS = [
  { key: "tv_floating_cabinet",       label: "TV + Floating Cabinet" },
  { key: "tv_fireplace",              label: "TV + Fireplace" },
  { key: "tv_fireplace_base_cabinet", label: "TV + Fireplace + Base Cabinet" },
  { key: "full_height_feature_wall",  label: "Full Height Feature Wall" },
];
